import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { shareActions } from '../../redux/slices/share.slice';
import { URN_QDOS_S3 } from '../../helpers/constans';

const MemorableMoments = (): ReactElement => {
  const dispatch = useDispatch();
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  const shareState = useSelector((state: RootState) => state.share);

  const memorableMoments = useMemo(() => {
    if (!Array.isArray(shareState?.memorableMoments)) return [];
    return shareState.memorableMoments;
  }, [shareState?.memorableMoments]);

  useEffect(() => {
    dispatch(shareActions.getMemorableMoments());
  }, [dispatch]);

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const renderMedia = (item: any, enlarged = false): ReactElement => {
    const imageUrl = getMediaUrl(item?.imageId);
    const videoUrl = getMediaUrl(item?.videoUrl);

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={item?.eventTitle || 'Joy media'}
          sx={{
            width: '100%',
            height: enlarged ? 'auto' : '220px',
            maxHeight: enlarged ? '75vh' : '220px',
            objectFit: 'cover',
            borderRadius: '8px',
            display: 'block',
            backgroundColor: '#f5f5f5',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography>{item?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  const handleRemove = (event: React.MouseEvent, id: string): void => {
    event.stopPropagation();
    dispatch(shareActions.deleteMemorableMoment(id));
  };

  return (
    <Box sx={{ flex: 1, p: '2rem' }}>
      <Typography variant="h4" sx={{ mb: '1.5rem' }}>
        Joy
      </Typography>

      {!memorableMoments.length ? (
        <Typography sx={{ color: '#666' }}>No Joy items found.</Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))',
          }}
        >
          {memorableMoments.map((item: any) => (
            <Box
              key={item._id}
              onClick={() => setSelectedItem(item)}
              sx={{
                border: '1px solid #ddd',
                borderRadius: '12px',
                p: '1rem',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, mb: '0.5rem' }}>
                {item.eventTitle || 'Joy item'}
              </Typography>

              <Typography sx={{ mb: '0.75rem' }}>
                {item.description || 'No description'}
              </Typography>

              <Box sx={{ mb: '0.75rem' }}>{renderMedia(item)}</Box>

              <Button
                size="small"
                color="error"
                onClick={(event) => handleRemove(event, item._id)}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '1rem',
            pt: '1rem',
          }}
        >
          <Typography variant="h6">
            {selectedItem?.eventTitle || 'Joy item'}
          </Typography>

          <IconButton onClick={() => setSelectedItem(null)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {selectedItem && (
            <Box>
              <Typography sx={{ mb: '1rem' }}>
                {selectedItem.description || 'No description'}
              </Typography>

              <Box sx={{ mb: '1rem' }}>{renderMedia(selectedItem, true)}</Box>

              <Button
                size="small"
                color="error"
                onClick={(event) => {
                  handleRemove(event, selectedItem._id);
                  setSelectedItem(null);
                }}
              >
                Remove
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MemorableMoments;